// IE Hub - Form/Schema Service
// Provides abstraction layer for form and schema-related API operations

import { httpGet, httpPost } from '../http';
import { formEndpoints, buildCustomEndpoint } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import { runEffects } from '../../../engine/actions/effects';
import type {
  ApiResponse,
  Schema,
  FormSubmitRequest,
  FormSubmitResponse,
  FormValidateRequest,
  FormValidateResponse,
  RequestConfig,
  ServiceResponse,
  ServiceListResponse
} from '../types';

/**
 * Form/Schema Service Class
 * Handles all form and schema-related API operations with proper error handling and monitoring
 */
export class FormService {
  // Schema cache for performance
  private static schemaCache = new Map<string, { schema: Schema; timestamp: number }>();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get list of available forms/schemas
   */
  static async getForms(config?: RequestConfig): ServiceListResponse<Schema> {
    try {
      const response = await httpGet<ServiceListResponse<Schema>>(
        formEndpoints.list,
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('form_list_error', error, {
          category: 'api',
          severity: 'warning',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get specific schema by ID with caching
   */
  static async getSchema(schemaId: string, config?: RequestConfig): ServiceResponse<Schema> {
    try {
      // Check cache first
      const cached = this.schemaCache.get(schemaId);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
        return {
          success: true,
          data: cached.schema,
          timestamp: new Date().toISOString()
        };
      }

      let schema: Schema | null = null;

      // Try API first
      try {
        const response = await httpGet<ApiResponse<Schema>>(
          formEndpoints.get(schemaId),
          {
            timeout: config?.timeout,
            headers: config?.headers
          }
        );

        if (response.data.success && response.data.data) {
          schema = response.data.data;
        }
      } catch (apiError) {
        console.warn(`API not available for schema ${schemaId}, trying local fallback`);
      }

      // Fallback to local schema files if API fails
      if (!schema) {
        try {
          switch (schemaId) {
            case 'exampleForm':
              schema = (await import('../../../engine/schema/examples/exampleForm.json')).default;
              break;
            case '2-refered-method':
              schema = (await import('../../../engine/schema/onboarding/2-refered-method.json')).default;
              break;
            case '3-refered-from-company':
              schema = (await import('../../../engine/schema/onboarding/3-refered-from-company.json')).default;
              break;
            case '4-reason-iehub':
              schema = (await import('../../../engine/schema/onboarding/4-reason-iehub.json')).default;
              break;
            case '5-personal-details':
              schema = (await import('../../../engine/schema/onboarding/5-personal-details.json')).default;
              break;
            case '6-date-of-birth':
              schema = (await import('../../../engine/schema/onboarding/6-date-of-birth.json')).default;
              break;
            case '7-contact-details':
              schema = (await import('../../../engine/schema/onboarding/7-contact-details.json')).default;
              break;
            case '8-address-details':
              schema = (await import('../../../engine/schema/onboarding/8-address-details.json')).default;
              break;
            case '9-vulnerability-circumstances':
              schema = (await import('../../../engine/schema/onboarding/9-vulnerability-circumstances.json')).default;
              break;
            default:
              throw new Error(`No local fallback available for schema: ${schemaId}`);
          }
        } catch (localError) {
          const errorMessage = localError instanceof Error ? localError.message : 'Unknown local error';
          throw new Error(`Failed to load schema from both API and local fallback: ${errorMessage}`);
        }
      }

      if (!schema) {
        throw new Error(`Schema ${schemaId} not found`);
      }

      // Validate schema structure
      const isValid = this.validateSchemaStructure(schema);
      if (!isValid) {
        throw new Error('Invalid schema structure');
      }

      // Cache the schema
      this.schemaCache.set(schemaId, { schema, timestamp: now });

      return {
        success: true,
        data: schema,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('form_schema_error', error, {
          category: 'api',
          severity: 'error',
          context: { schemaId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Submit form data
   */
  static async submitForm(
    schemaId: string,
    formData: Record<string, any>,
    options: {
      navigate?: (to: string, options?: { replace?: boolean }) => void;
      metadata?: Record<string, any>;
      config?: RequestConfig;
    } = {}
  ): ServiceResponse<FormSubmitResponse> {
    try {
      // Get schema for submission configuration
      const schemaResponse = await this.getSchema(schemaId, { skipErrorCapture: true });
      const schema = schemaResponse.data;

      if (!schema) {
        throw new Error(`Schema ${schemaId} not found`);
      }

      // Prepare submission data
      const submitData: FormSubmitRequest = {
        schemaId,
        data: formData,
        timestamp: new Date().toISOString(),
        clientMetadata: {
          userAgent: navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...options.metadata
        }
      };

      // Determine submission endpoint
      const submitUrl = schema.actions?.submit?.url || formEndpoints.submit(schemaId);
      const submitMethod = schema.actions?.submit?.method || 'POST';

      // Submit data using the configured method
      let response: { data: ApiResponse<FormSubmitResponse> };
      
      if (submitMethod === 'POST') {
        response = await httpPost<ApiResponse<FormSubmitResponse>>(
          submitUrl,
          submitData,
          {
            timeout: options.config?.timeout,
            headers: options.config?.headers
          }
        );
      } else {
        // For other methods, use the custom endpoint builder
        const endpoint = buildCustomEndpoint(submitUrl.replace('/api/v1/', ''));
        response = await httpPost<ApiResponse<FormSubmitResponse>>(
          endpoint,
          submitData,
          {
            timeout: options.config?.timeout,
            headers: options.config?.headers
          }
        );
      }

      // Handle success actions via effects registry
      if (schema.actions?.onSuccess && options.navigate) {
        await runEffects(
          schema.actions.onSuccess,
          { navigate: options.navigate },
          { 
            result: response.data.data,
            schemaId,
            formData 
          }
        );
      }

      return response.data;

    } catch (error) {
      // Handle error actions via effects registry
      try {
        const schemaResponse = await this.getSchema(schemaId, { skipErrorCapture: true });
        const schema = schemaResponse.data;
        
        if (schema?.actions?.onError && options.navigate) {
          await runEffects(
            schema.actions.onError,
            { navigate: options.navigate },
            {
              error: error instanceof Error ? error.message : 'Unknown error',
              schemaId,
              formData
            }
          );
        }
      } catch (effectError) {
        console.warn('Failed to execute error effects:', effectError);
      }

      if (!options.config?.skipErrorCapture) {
        femaCapture('form_submit_error', error, {
          category: 'api',
          severity: 'error',
          context: { schemaId, formDataKeys: Object.keys(formData) },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Validate form data
   */
  static async validateForm(
    schemaId: string,
    formData: Record<string, any>,
    fieldName?: string,
    config?: RequestConfig
  ): ServiceResponse<FormValidateResponse> {
    try {
      const validateData: FormValidateRequest = {
        schemaId,
        data: formData,
        fieldName
      };

      const response = await httpPost<ApiResponse<FormValidateResponse>>(
        formEndpoints.validate(schemaId),
        validateData,
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('form_validate_error', error, {
          category: 'api',
          severity: 'warning',
          context: { schemaId, fieldName },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get form preview/render data
   */
  static async getFormPreview(schemaId: string, config?: RequestConfig): ServiceResponse<Schema> {
    try {
      const response = await httpGet<ApiResponse<Schema>>(
        formEndpoints.preview(schemaId),
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('form_preview_error', error, {
          category: 'api',
          severity: 'warning',
          context: { schemaId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Clear schema cache
   */
  static clearCache(schemaId?: string): void {
    if (schemaId) {
      this.schemaCache.delete(schemaId);
    } else {
      this.schemaCache.clear();
    }
  }

  /**
   * Get cached schema if available and not expired
   */
  static getCachedSchema(schemaId: string): Schema | null {
    const cached = this.schemaCache.get(schemaId);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      return cached.schema;
    }
    
    return null;
  }

  /**
   * Validate schema structure (basic validation)
   */
  private static validateSchemaStructure(schema: Schema): boolean {
    try {
      // Basic structure validation
      if (!schema || typeof schema !== 'object') {
        return false;
      }

      // Check for either old or new schema format
      const hasOldFormat = 'id' in schema && 'layout' in schema;
      const hasNewFormat = 'formName' in schema && 'sections' in schema;

      if (!hasOldFormat && !hasNewFormat) {
        console.error('Schema must have either old format (id, layout) or new format (formName, sections)');
        return false;
      }

      // Validate version
      if ('version' in schema) {
        // For new format, version can be a number
        if (hasNewFormat && typeof schema.version === 'number') {
          // Valid number version
        } else if (typeof schema.version === 'string' && schema.version.match(/^\d+(\.\d+\.\d+)?$/)) {
          // Valid string version (semantic or simple)
        } else {
          console.error(`Invalid version format: ${schema.version}`);
          return false;
        }
      }

      // Validate structure based on format
      if (hasNewFormat) {
        // New format validation
        if (!Array.isArray(schema.sections)) {
          console.error('Sections must be an array');
          return false;
        }

        // Validate each section has required fields
        for (const section of schema.sections) {
          if (!section.sectionName || !Array.isArray(section.fields)) {
            console.error('Each section must have sectionName and fields array');
            return false;
          }
        }
      } else {
        // Old format validation
        if (!schema.layout || typeof schema.layout !== 'object') {
          console.error('Invalid layout structure');
          return false;
        }

        if (schema.layout.fields && !Array.isArray(schema.layout.fields)) {
          console.error('Fields must be an array');
          return false;
        }
      }

      return true;
    } catch (err) {
      console.error('Schema validation error:', err);
      return false;
    }
  }

  /**
   * Preload commonly used schemas
   */
  static async preloadSchemas(schemaIds: string[]): Promise<void> {
    const promises = schemaIds.map(id => 
      this.getSchema(id, { skipErrorCapture: true }).catch(error => {
        console.warn(`Failed to preload schema ${id}:`, error);
      })
    );

    await Promise.allSettled(promises);
  }
}

export default FormService;
